/**
 * STEMO: Cart-first checkout guard.
 * - Redirects direct /checkout visits when B2B attributes are missing/invalid
 * - Validates cart form before save/checkout submit
 * - Intercepts checkout links → /cart
 */
(function () {
  const ATTR_KEYS = {
    customerType: "customer_type",
    companyName: "foretagsnamn",
    orgNumber: "org_nummer",
    invoiceEmail: "faktureringsmejl",
  };

  function getConfig() {
    return window.StemoCheckoutConfig || {};
  }

  function getRoutesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function getCountryCode() {
    const cfg = getConfig();
    if (cfg.countryCode) return cfg.countryCode;
    return (window.Shopify && window.Shopify.country) || "";
  }

  function attributesToMap(attributes) {
    const map = {};
    if (!Array.isArray(attributes)) return map;
    attributes.forEach((item) => {
      if (item && item.key) map[item.key] = (item.value || "").trim();
    });
    return map;
  }

  function normalizeOrgNumber(value) {
    return String(value || "").replace(/[\s-]/g, "");
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  }

  function isValidOrgNumber(value) {
    // Format validation is handled downstream (webhook/ERP). Require non-empty only.
    return normalizeOrgNumber(value).length >= 1;
  }

  function isValidCompanyName(value) {
    return String(value || "").trim().length >= 2;
  }

  function isCheckoutReady(attrs, countryCode) {
    const type = String(attrs[ATTR_KEYS.customerType] || "").trim();
    if (!type) return false;
    if (countryCode === "SE" && type === "Privatperson") return true;
    if (type !== "Foretag") return false;

    return (
      isValidCompanyName(attrs[ATTR_KEYS.companyName]) &&
      isValidOrgNumber(attrs[ATTR_KEYS.orgNumber]) &&
      isValidEmail(attrs[ATTR_KEYS.invoiceEmail])
    );
  }

  function getFormValues() {
    const typeEl = document.getElementById("stemo-ct-value");
    return {
      customer_type: typeEl ? typeEl.value : "Foretag",
      foretagsnamn: (document.getElementById("stemo-foretagsnamn")?.value || "").trim(),
      org_nummer: (document.getElementById("stemo-org-nr")?.value || "").trim(),
      faktureringsmejl: (document.getElementById("stemo-faktura-mejl")?.value || "").trim(),
    };
  }

  function validateFormValues(values) {
    const cfg = getConfig();
    const messages = cfg.messages || {};
    const countryCode = getCountryCode();

    if (!values.customer_type) {
      return { valid: false, fieldId: "stemo-ct-buttons", message: messages.selectType };
    }

    if (countryCode === "SE" && values.customer_type === "Privatperson") {
      return { valid: true };
    }

    if (values.customer_type !== "Foretag") {
      return { valid: false, fieldId: "stemo-ct-buttons", message: messages.selectType };
    }

    if (!isValidCompanyName(values.foretagsnamn)) {
      return { valid: false, fieldId: "stemo-foretagsnamn", message: messages.companyName };
    }
    if (!isValidOrgNumber(values.org_nummer)) {
      return { valid: false, fieldId: "stemo-org-nr", message: messages.orgNumber };
    }
    if (!isValidEmail(values.faktureringsmejl)) {
      return { valid: false, fieldId: "stemo-faktura-mejl", message: messages.invoiceEmail };
    }

    return { valid: true };
  }

  function shake(el) {
    if (!el) return;
    el.classList.remove("stemo-shake");
    void el.offsetWidth;
    el.classList.add("stemo-shake");
    el.addEventListener("animationend", () => el.classList.remove("stemo-shake"), { once: true });
  }

  function showFieldError(fieldId) {
    const el = document.getElementById(fieldId);
    if (el) {
      el.classList.add("stemo-invalid");
      shake(el);
      if (el.focus) el.focus();
    }
  }

  function validateCartForm() {
    const result = validateFormValues(getFormValues());
    if (!result.valid) {
      showFieldError(result.fieldId);
    }
    return result.valid;
  }

  function isCheckoutPath(pathname) {
    return /\/checkout\/?/.test(pathname || "");
  }

  function isCartPath(pathname) {
    return /\/cart\/?$/.test(pathname || "");
  }

  async function fetchCart() {
    const response = await fetch(`${getRoutesRoot()}cart.js`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return null;
    return response.json();
  }

  async function guardCheckoutPage() {
    if (!isCheckoutPath(window.location.pathname)) return;

    const cart = await fetchCart();
    if (!cart) return;

    // Avoid redirect loop: empty cart on /checkout should not bounce to empty /cart.
    if (!cart.item_count || cart.item_count === 0) return;

    const attrs = attributesToMap(cart.attributes);
    if (!isCheckoutReady(attrs, getCountryCode())) {
      window.location.replace(`${getRoutesRoot()}cart`);
    }
  }

  function guardCheckoutLinks() {
    document.addEventListener(
      "click",
      async (event) => {
        if (isCartPath(window.location.pathname)) return;

        const path = event.composedPath ? event.composedPath() : [event.target];
        for (let i = 0; i < path.length; i++) {
          const el = path[i];
          if (el.tagName === "A" && el.href && el.href.indexOf("/checkout") !== -1) {
            const cart = await fetchCart();
            if (!cart || !cart.item_count) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            window.location.href = `${getRoutesRoot()}cart`;
            return;
          }
        }
      },
      true
    );
  }

  function guardCheckoutForms() {
    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const submitter = event.submitter;
        if (!submitter || submitter.name !== "checkout") return;
        if (form.classList.contains("cart-form")) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.href = `${getRoutesRoot()}cart`;
      },
      true
    );
  }

  function guardCartForm() {
    const form = document.querySelector(".cart-form");
    if (!form) return;

    form.addEventListener(
      "submit",
      (event) => {
        const submitter = event.submitter;
        const isCheckout = submitter && submitter.name === "checkout";
        const isUpdate = submitter && submitter.name === "update";
        if (!isCheckout && !isUpdate) return;

        const values = getFormValues();
        if (getCountryCode() === "SE" && values.customer_type === "Privatperson") {
          ["stemo-foretagsnamn", "stemo-org-nr", "stemo-faktura-mejl", "stemo-moms-nr", "stemo-referens"].forEach(
            (id) => {
              const el = document.getElementById(id);
              if (el) el.value = "";
            }
          );
        }

        if (!validateCartForm()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    ["stemo-foretagsnamn", "stemo-org-nr", "stemo-faktura-mejl"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener("input", () => el.classList.remove("stemo-invalid"));
      }
    });
  }

  function initCustomerTypeToggle() {
    const buttons = document.querySelectorAll(".stemo-ct-btn");
    const hidden = document.getElementById("stemo-ct-value");
    const fields = document.getElementById("stemo-company-fields");
    if (!hidden || !fields) return;

    function setActive(type) {
      hidden.value = type;
      buttons.forEach((btn) => {
        btn.classList.toggle("active", btn.getAttribute("data-type") === type);
      });
      fields.classList.toggle("visible", type === "Foretag");
    }

    buttons.forEach((btn) => {
      btn.addEventListener("click", () => setActive(btn.getAttribute("data-type")));
    });
  }

  window.StemoCheckout = {
    ATTR_KEYS,
    attributesToMap,
    isCheckoutReady,
    validateFormValues,
    isValidOrgNumber,
    isValidEmail,
    isValidCompanyName,
  };

  guardCheckoutLinks();
  guardCheckoutForms();
  guardCheckoutPage();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      guardCartForm();
      initCustomerTypeToggle();
    });
  } else {
    guardCartForm();
    initCustomerTypeToggle();
  }
})();
